import { isFrontmatterLine } from './is-frontmatter-line.util';
import type { ClarityFrontmatterSplit } from './models/clarity-frontmatter-split.model';

const FENCE = '---';
const NOT_FOUND = -1;

/** Index of the opening fence, tolerating leading blank lines, or {@link NOT_FOUND}. */
function findOpeningFence(lines: readonly string[]): number {
	let index = 0;

	while (index < lines.length && lines[index].trim() === '') {
		index += 1;
	}

	return index < lines.length && lines[index].trim() === FENCE ? index : NOT_FOUND;
}

/**
 * Index of the closing fence, or {@link NOT_FOUND}.
 *
 * The scan is bounded to lines that still look like frontmatter. Searching the whole file for the
 * next `---` made any document with a broken opening block swallow everything up to its first
 * horizontal rule, and the viewer then rendered only the tail.
 */
function findClosingFence(lines: readonly string[], open: number): number {
	for (let index = open + 1; index < lines.length; index += 1) {
		if (lines[index].trim() === FENCE) {
			return index;
		}

		if (!isFrontmatterLine(lines[index])) {
			return NOT_FOUND;
		}
	}

	return NOT_FOUND;
}

/**
 * Character offset just past line `last`'s terminator, clamped to the content length.
 *
 * Measuring against the original string, rather than re-joining the split lines, is what keeps the
 * `head + body === content` guarantee for CRLF files.
 */
function offsetPastLine(lines: readonly string[], last: number, contentLength: number): number {
	let offset = 0;

	for (let index = 0; index <= last; index += 1) {
		offset += lines[index].length + 1;
	}

	return Math.min(offset, contentLength);
}

/**
 * Split a leading `---…---` frontmatter block off markdown content.
 *
 * Guarantees `head + body === content` byte for byte, CRLF included, so a caller that edits only the
 * body can reassemble the file without touching the block. When there is no closed leading fence,
 * `head` is `''` and `body` is the whole content.
 *
 * Ported from Clarity `apps/web/src/shared/utils/frontmatter.ts`, which in turn mirrors the server's
 * `parseFrontmatter.js` — including the bounded closing-fence scan, so the two agree on what counts
 * as a block. Divergence there is a rendering bug, so the fence rules are not tidied.
 */
export function splitFrontmatter(content: string): ClarityFrontmatterSplit {
	if (!content) {
		return { head: '', body: content };
	}

	const lines = content.split('\n');
	const open = findOpeningFence(lines);

	if (open === NOT_FOUND) {
		return { head: '', body: content };
	}

	const close = findClosingFence(lines, open);

	if (close === NOT_FOUND) {
		return { head: '', body: content };
	}

	const offset = offsetPastLine(lines, close, content.length);

	return { head: content.slice(0, offset), body: content.slice(offset) };
}
